/**
 * Independently reversible roster-dispatch rollout controls.
 *
 * Database keys intentionally match this map one-for-one. Missing rows are
 * always false so a partial migration or read failure cannot enable a new
 * production mutation path.
 */
export const DISPATCH_RELIABILITY_FLAG_DB_KEYS = {
  INTENT_PREFLIGHT: 'roster_dispatch_intent_preflight_v1',
  BOUNDED_FEASIBILITY: 'roster_dispatch_bounded_feasibility_v1',
  ASSIGNMENT_IDEMPOTENCY: 'roster_assignment_idempotency_v1',
  NOTIFICATION_DELIVERY: 'roster_assignment_notification_delivery_v1',
  TIMESHEET_PAY_ADVISORY: 'timesheet_pay_advisory_v1',
  /** UX-001 — stop offering mid-shift intents that perform no change. */
  MID_SHIFT_INTENT_HONESTY: 'roster_dispatch_intent_change_containment_v1',
  /** WFR-001 — reason-level first-shift readiness. Off preserves today's WARN. */
  FIRST_SHIFT_READINESS_POLICY: 'roster_first_shift_readiness_policy_v1',
  /** WFR-002 — lawful approval fallback for a single-admin provider. */
  SINGLE_ADMIN_APPROVAL_FALLBACK: 'roster_single_admin_approval_fallback_v1',
  /** RCH-001 — every bounded current-page candidate is reachable, not just the top five. */
  CANDIDATE_REACHABILITY: 'roster_dispatch_candidate_reachability_v1',
  /** Master exception-first paint + Monday week chrome. Default off. */
  MASTER_EXCEPTION_QUEUE: 'roster_master_exception_queue_v1',
  /** Master APPEND_MISSING UI/mode. Default off. Does not gate 4b. */
  MASTER_DEPARTMENT_APPLY: 'roster_master_department_apply_v1',
} as const;

/** Historical fixture IDs for tests only. Do not gate production behaviour on these. */
export const MEDEA_PARK_TENANT_ID = '7ded4fe4-291e-4ec9-851e-66f914399b1e';

export type DispatchReliabilityFlagKey = keyof typeof DISPATCH_RELIABILITY_FLAG_DB_KEYS;

const DISPATCH_RELIABILITY_FLAG_DB_KEY_SET = new Set<string>(
  Object.values(DISPATCH_RELIABILITY_FLAG_DB_KEYS),
);

export const DISPATCH_RELIABILITY_FLAG_KEYS = Object.keys(
  DISPATCH_RELIABILITY_FLAG_DB_KEYS,
) as DispatchReliabilityFlagKey[];

export type DispatchReliabilityFlags = Record<DispatchReliabilityFlagKey, boolean>;

export const DISPATCH_RELIABILITY_FLAGS_DEFAULT: DispatchReliabilityFlags = {
  INTENT_PREFLIGHT: false,
  BOUNDED_FEASIBILITY: false,
  ASSIGNMENT_IDEMPOTENCY: false,
  NOTIFICATION_DELIVERY: false,
  TIMESHEET_PAY_ADVISORY: false,
  MID_SHIFT_INTENT_HONESTY: false,
  FIRST_SHIFT_READINESS_POLICY: false,
  SINGLE_ADMIN_APPROVAL_FALLBACK: false,
  CANDIDATE_REACHABILITY: false,
  MASTER_EXCEPTION_QUEUE: false,
  MASTER_DEPARTMENT_APPLY: false,
};

export function isDispatchReliabilityFlagEnabled(
  flags: Partial<DispatchReliabilityFlags> | null | undefined,
  key: DispatchReliabilityFlagKey,
): boolean {
  return Boolean(flags?.[key]);
}

/**
 * These rollout controls are tenant opt-ins, never platform or plan defaults.
 * Keeping this predicate client-safe lets every flag writer and resolver share
 * the same containment rule.
 */
export function isTenantScopedDispatchReliabilityFlagDbKey(
  flagKey: string | null | undefined,
): boolean {
  return DISPATCH_RELIABILITY_FLAG_DB_KEY_SET.has(String(flagKey ?? '').trim());
}

/**
 * Contain a tenant's dispatch-reliability flags until UAT closes, except
 * keys listed on that tenant's authored rollout state. Missing or implied
 * rollout state is not contained — do not re-introduce a tenant UUID here.
 */
export function isContainedDispatchReliabilityEnablement(
  flagKey: string | null | undefined,
  rollout:
    | {
        containedUntilUat: boolean;
        releasedDispatchReliabilityFlagKeys: readonly string[];
      }
    | null
    | undefined,
): boolean {
  if (!rollout?.containedUntilUat) return false;
  const key = String(flagKey ?? '').trim();
  if (rollout.releasedDispatchReliabilityFlagKeys.includes(key)) return false;
  return isTenantScopedDispatchReliabilityFlagDbKey(key);
}
